import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * The shape of the chatbot context, managing the currently selected chatbot agent.
 */
interface ChatbotContextType {
  /** The index of the currently selected chatbot in the allowedBots list. */
  selectedChatbotValue: number;
  /** Function to update the selected chatbot index. */
  setSelectedChatbotValue: (value: number) => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

/**
 * Custom hook to access the chatbot context.
 * Must be used within a ChatbotProvider.
 * 
 * @throws {Error} If used outside of a ChatbotProvider.
 * @returns {ChatbotContextType} The chatbot context value.
 */
export const useChatbotContext = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbotContext must be used within a ChatbotProvider');
  }
  
  return context;
};

/**
 * Props for the ChatbotProvider component.
 */
interface ChatbotProviderProps {
  /** The child components that will have access to the chatbot context. */
  children?: ReactNode;
}

/**
 * Provides state management for the selected chatbot agent across the application.
 * 
 * @param {ChatbotProviderProps} props - The component props.
 * @returns {JSX.Element} The context provider component.
 */
export const ChatbotProvider = ({ children }: ChatbotProviderProps) => {
  // Initialize state with the first chatbot (index 0) selected by default.
  const [selectedChatbotValue, setSelectedChatbotValue] = useState<number>(0);
  
  return (
    <ChatbotContext.Provider value={{ selectedChatbotValue, setSelectedChatbotValue }}>
      {children}
    </ChatbotContext.Provider>
  );
};
